import { defineStore } from 'pinia';
import { usePinia } from '#imports';
import { handleErrorAndNotificationHandler } from '~/util/errorNotificationHandler';
import type { Pinia } from './shared/sharedTypes';
import { DateTime } from 'luxon';
import { timezones } from '~/util';

interface BasicUserInfo {
  displayName: string;
  displayPrefix?: string;
  displaySuffix?: string;
  emailAddress: string;
  firstName: string;
  jobTitle?: string;
  languageId?: string;
  lastName: string;
  middleName?: string;
  portraitUrl?: string;
  prefixId?: number;
  suffixId?: number;
  timezoneId?: string;
  userId: number;
  uuid?: string;
}

interface Summary {
  acceptedTermsOfService: boolean;
  basicUserInfo: BasicUserInfo;
  contactData: Record<string, string | number | boolean | null>;
  lastLoginDateTimeMillis: number;
  missingInviteAsNewUser: boolean;
  verifiedEmail: boolean;
}

interface ClientAction {
  href: string;
  method: string;
}

interface Phone {
  phoneId: number;
  contactTypeDisplayName: string;
  number: string;
  primary: boolean;
  extension?: string;
}

interface Address {
  addressId: number;
  contactTypeDisplayName: string;
  countryId: number;
  countryName: string;
  locality?: string;
  region?: string;
  postalCode?: string;
  primary: boolean;
  buildingName?: string;
  streetAddressLine1?: string;
  streetAddressLine2?: string;
  streetAddressLine3?: string;
}

interface Client {
  actions: Record<string, ClientAction>;
  addresses: Address[];
  adminUserIds: number[];
  clientId: number;
  clinicalTrialEntityId?: number;
  name: string;
  organizationId: number;
  phoneNumbers: Phone[];
  uuid: string;
}

interface User {
  access_token: string;
  actions: Record<string, ClientAction>;
  clients: Client[];
  email: string;
  image: string;
  name: string;
  sub: string;
  summary: Summary;
  nonEecUser?: boolean;
  nonProvisionedUser?: boolean;
}
interface UserState {
  user: User | null;
}

interface API {
  user: {
    updateUserProfile(): string;
  };
  clients: {
    getClient(clientId: number): string;
  };
}

const normalizeTimezoneId = (timezoneId?: string): string | undefined => {
  if (!timezoneId) return timezoneId;

  // Only normalize legacy Etc/GMT zones
  if (!timezoneId.startsWith('Etc/GMT')) {
    return timezoneId;
  }

  const offset = DateTime.now().setZone(timezoneId).toFormat('ZZ');

  // Find matching modern zone with same offset
  const match = timezones.find((tz) =>
    tz.utc?.some(
      (zone: string) =>
        !zone.startsWith('Etc/GMT') &&
        DateTime.now().setZone(zone).toFormat('ZZ') === offset
    )
  );

  return (
    match?.utc?.find((zone: string) => !zone.startsWith('Etc/GMT')) ||
    timezoneId
  );
};

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    user: null,
  }),

  actions: {
    setUser(user: User) {
      if (user?.summary?.basicUserInfo?.timezoneId) {
        user.summary.basicUserInfo.timezoneId = normalizeTimezoneId(
          user.summary.basicUserInfo.timezoneId
        );
      }

      this.user = user;
    },

    async updateUser(userObject: Partial<Summary>) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp = await pinia.$axios.$patch<Summary>(
          pinia.$API.user.updateUserProfile(),
          userObject
        );

        if (this.user && resp) {
          this.user.summary.acceptedTermsOfService =
            resp.acceptedTermsOfService;
          this.user.summary.verifiedEmail = resp.verifiedEmail;
          this.user.summary.missingInviteAsNewUser =
            resp.missingInviteAsNewUser;
          this.user.summary.lastLoginDateTimeMillis =
            resp.lastLoginDateTimeMillis;
          this.user.summary.basicUserInfo = {
            ...resp.basicUserInfo,
            timezoneId: normalizeTimezoneId(resp.basicUserInfo.timezoneId),
          };
          this.user.summary.contactData = resp.contactData;
        }
      } catch (error) {
        handleErrorAndNotificationHandler('Error updating user:', error);
      }
    },

    removeClient(clientId: number) {
      if (this.user) {
        this.user.clients = this.user.clients.filter(
          (client) => client.clientId !== clientId
        );
      }
    },

    async getUserClients(clientId: number) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const resp = await pinia.$axios.$get<Client>(
          pinia.$API.clients.getClient(clientId)
        );

        if (
          this.user &&
          !this.user.clients.some((client) => client.clientId === resp.clientId)
        ) {
          this.user.clients.push(resp);
        }
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching clients for the user',
          error
        );
      }
    },
  },
  getters: {
    getUserGetter: (state: UserState): UserState['user'] => state.user,
  },
});
